import {
  accountNumberSchema,
  accountTypeOptionsSchema,
  ifscCodeSchema,
} from "creso-core/zod";
import {
  Form,
  FormField,
  FormInput,
  FormSelect,
  useMultiStepForm,
  Button,
} from "web-design-system";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { DISTRIBUTOR_BANK_ACCOUNT_TYPE_OPTIONS } from "creso-core/constants";
import { useCreateDistributorBankDetails } from "creso-core/hooks";
import { InfoIcon } from "lucide-react";
import { handleError } from "@/utils/error";

const formSchema = z
  .object({
    account_number: accountNumberSchema,
    cnf_acc_number: accountNumberSchema,
    ifsc: ifscCodeSchema,
    type: accountTypeOptionsSchema,
  })
  .refine((data) => data.account_number === data.cnf_acc_number, {
    path: ["cnf_acc_number"],
    message: "Both account numbers must match",
  });

type FormValues = z.infer<typeof formSchema>;

export function BankDetailsForm() {
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
  });
  const { nextStep } = useMultiStepForm<FormValues>();
  const createDistributorBankDetails = useCreateDistributorBankDetails();

  const onSubmit = async (values: FormValues) => {
    try {
      await createDistributorBankDetails.mutateAsync({
        account_number: values.account_number,
        ifsc_code: values.ifsc,
        account_type: values.type,
      });
      nextStep();
    } catch (error) {
      handleError(error, "Failed to create bank details");
    }
  };

  return (
    <div className="flex flex-col gap-5">
      <span className="text-lg font-semibold">Bank details</span>
      <Form {...form}>
        <form
          className="flex flex-col justify-between flex-1 gap-5"
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <div className="flex flex-col gap-6 pb-8">
            <FormField
              control={form.control}
              name="account_number"
              render={({ field }) => (
                <FormInput
                  type="number"
                  placeholder="1234567890"
                  label="Account number"
                  {...field}
                  className="gap-2"
                />
              )}
            />
            <FormField
              control={form.control}
              name="cnf_acc_number"
              render={({ field }) => (
                <FormInput
                  placeholder="1234567890"
                  type="number"
                  label="Confirm account number"
                  {...field}
                  className="gap-2"
                />
              )}
            />
            <FormField
              control={form.control}
              name="ifsc"
              render={({ field }) => (
                <FormInput
                  placeholder="SBIN0000489"
                  label="IFSC code"
                  {...field}
                  className="gap-2"
                />
              )}
            />
            <FormField
              control={form.control}
              name="type"
              render={({ field }) => (
                <FormSelect
                  label="Account type"
                  {...field}
                  placeholder="Select"
                  options={DISTRIBUTOR_BANK_ACCOUNT_TYPE_OPTIONS}
                  className="gap-2"
                />
              )}
            />
          </div>

          <div className="flex flex-col gap-5">
            <div className="flex items-start gap-2 rounded-lg bg-tag-slate p-3 border border-border">
              <InfoIcon className="size-4 mt-0.5 shrink-0 text-tag-slate-foreground" />
              <span className="text-xs text-tag-slate-foreground">
                These details will be used to process your commissions once you
                start your business.
              </span>
            </div>
            <div className="flex gap-5">
              <Button
                size="lg"
                type="button"
                onClick={nextStep}
                variant="outline"
              >
                Skip
              </Button>
              <Button size="lg" className="flex-1" type="submit">
                Next
              </Button>
            </div>
          </div>
        </form>
      </Form>
    </div>
  );
}
